import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Plus, Settings } from "lucide-react";
import { getRoleOrders } from "@/utils/roleUtils";
import SegmentedTabs from "@/components/SegmentedTabs";
import type {
    EquityProps,
    RoleGroupColumn,
    SummaryColumn,
    TableColumn,
    TableData,
} from "./equityInterfaces";
import {
    annualToTableData,
    computeRoleGroupPrefill,
    equityToTableData,
    generatedScheduleResultToEquity,
    roleGroupToTableData,
    summaryToTableData,
    weeklyToTableData,
    withDefaultAnnualColumns
} from "./equityTableData";
import {
    fetchAnnualConfig,
    fetchEquityConfig,
    fetchEquityTargets,
    fetchExtendedWgData,
    fetchRoleGroupConfig,
    fetchSummaryColumns,
    toggleEquityActive,
    updateAnnualConfig,
    updateEquityTargets,
    updateRoleGroupConfig,
    updateSummaryColumns
} from "./api";
import { Switch } from "@/components/forms/Switch";
import FilterButton from "@/components/button/FilterButton";
import TimelineFilterOverlay from "@/components/overlays/TimelineFilterOverlay";
import IconButton from "@/components/button/IconButton";
import Button from "@/components/button/Button";
import EditableColumnHeader from "@/components/equity/EditableColumnHeader";
import EquityTargetCell from "@/components/equity/EquityTargetCell";
import SummaryColumnModal from "@/components/modals/SummaryColumnModal";
import EquityConfigModal from "@/components/modals/EquityConfigModal";
import RefDaysModal from "@/components/modals/RefDaysModal";
import type { EquityTargetData } from "./types/needsContrainsRequest";
import type { BusinessAccountExtendedWgItemData } from "./types/needsContrains";

//for temporary display of equity
const params = new URLSearchParams(window.location.search);
const bizId = params.get("bizId")
const wgId = params.get("wgId")
const depId = `${bizId + "_" + wgId}`

type EquityDisplayMode = 'ratio' | 'max' | 'summary' | 'roleGroup' | 'annual' | 'weekly';

const SchedulerEquity = ({ schedule }: EquityProps) => {
    const [targets, setTargets] = useState<EquityTargetData>({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [displayMode, setDisplayMode] = useState<EquityDisplayMode>('ratio');
    const [ratioActive, setRatioActive] = useState(true);
    const [maxActive, setMaxActive] = useState(true);
    const [summaryActive, setSummaryActive] = useState(true);
    const [summaryColumns, setSummaryColumns] = useState<SummaryColumn[]>([]);
    const [summaryModalOpen, setSummaryModalOpen] = useState(false);
    const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
    const [roleGroupActive, setRoleGroupActive] = useState(true);
    const [roleGroupColumns, setRoleGroupColumns] = useState<RoleGroupColumn[]>([]);
    const [roleGroupRefDays, setRoleGroupRefDays] = useState(28);
    const [roleGroupDesiredTargets, setRoleGroupDesiredTargets] = useState<Record<string, number>>({});
    const [needsData, setNeedsData] = useState<BusinessAccountExtendedWgItemData | null>(null);
    const [roleGroupModalOpen, setRoleGroupModalOpen] = useState(false);
    const [editingRoleGroupColumnId, setEditingRoleGroupColumnId] = useState<string | null>(null);
    const [refDaysModalOpen, setRefDaysModalOpen] = useState(false);
    const [annualColumns, setAnnualColumns] = useState<SummaryColumn[]>([]);
    const [annualSeededRoleIds, setAnnualSeededRoleIds] = useState<string[]>([]);
    // Seeded defaults depend on the schedule on screen: they are only stored once the user edits the annual columns.
    const [annualColumnsEdited, setAnnualColumnsEdited] = useState(false);
    // Stays false when the load failed, so that a later save cannot overwrite the stored columns with an empty list.
    const [annualColumnsLoaded, setAnnualColumnsLoaded] = useState(false);
    const [annualModalOpen, setAnnualModalOpen] = useState(false);
    const [editingAnnualColumnId, setEditingAnnualColumnId] = useState<string | null>(null);
    const [equityConfigOpen, setEquityConfigOpen] = useState(false);
    // null means "untouched filter", i.e. everything is selected — including entries loaded later.
    const [userSelection, setUserSelection] = useState<Set<string> | null>(null);
    const [roleSelection, setRoleSelection] = useState<Set<string> | null>(null);
    const [userFilterOpen, setUserFilterOpen] = useState(false);
    const [roleFilterOpen, setRoleFilterOpen] = useState(false);

    useEffect(() => {
        const loadConfig = async () => {
            try {
                const [config, cols, roleGroup, annualConfig] = await Promise.all([
                    fetchEquityConfig(),
                    fetchSummaryColumns(),
                    fetchRoleGroupConfig(),
                    fetchAnnualConfig().catch((err) => {
                        console.error("Failed to load annual columns:", err);
                        return null;
                    }),
                ]);
                setRatioActive(config.ratioActive);
                setMaxActive(config.maxActive);
                setSummaryActive(config.summaryActive ?? true);
                setRoleGroupActive(config.roleGroupActive ?? true);
                setSummaryColumns(cols);
                setRoleGroupColumns(roleGroup.columns);
                setRoleGroupRefDays(roleGroup.refDays);
                setRoleGroupDesiredTargets(roleGroup.desiredTargets);
                if (annualConfig) {
                    setAnnualColumns(annualConfig.columns);
                    setAnnualSeededRoleIds(annualConfig.seededRoleIds);
                    setAnnualColumnsLoaded(true);
                }
            } catch (err) {
                console.error("Failed to load equity config:", err);
            }
        };

        loadConfig();
    }, []);

    useEffect(() => {
        const loadTargets = async () => {
            try {
                const existingTargets = await fetchEquityTargets();
                setTargets(existingTargets || {});
            } catch (err) {
                console.error("Failed to load equity targets:", err);
            }
        };
        loadTargets();
    }, []);

    useEffect(() => {
        fetchExtendedWgData()
            .then(setNeedsData)
            .catch((err) => console.error("Failed to load needs data:", err));
    }, []);

    // Unfiltered and independent of displayMode: it is the source of the filter options on every tab.
    const equityResult = useMemo(() => generatedScheduleResultToEquity(schedule), [schedule]);
    const roleOrders = useMemo(() => getRoleOrders(schedule), [schedule]);

    useEffect(() => {
        if (!annualColumnsLoaded) {
            return;
        }
        const seeded = withDefaultAnnualColumns(annualColumns, annualSeededRoleIds, equityResult, roleOrders);
        if (seeded) {
            setAnnualColumns(seeded.columns);
            setAnnualSeededRoleIds(seeded.seededRoleIds);
        }
    }, [annualColumnsLoaded, annualColumns, annualSeededRoleIds, equityResult, roleOrders]);

    const equityTableData = useMemo(
        () => equityToTableData(equityResult, roleOrders, targets),
        [equityResult, roleOrders, targets]
    );

    const userFilterItems = useMemo(
        () => equityTableData.rows
            .filter((row) => row.columns[0]?.userId)
            .map((row) => ({ id: row.columns[0]!.userId!, label: row.columns[0]!.value })),
        [equityTableData]
    );

    const roleFilterItems = useMemo(
        () => equityTableData.columns
            .filter((col) => col.type === 'role' && col.id)
            .map((col) => ({ id: col.id!, label: col.label })),
        [equityTableData]
    );

    const selectedUserIds = useMemo(
        () => userSelection ?? new Set(userFilterItems.map((item) => item.id)),
        [userSelection, userFilterItems]
    );

    const selectedRoleIds = useMemo(
        () => roleSelection ?? new Set(roleFilterItems.map((item) => item.id)),
        [roleSelection, roleFilterItems]
    );

    const userFilterCount = selectedUserIds.size === userFilterItems.length ? 0 : selectedUserIds.size;
    const roleFilterCount = selectedRoleIds.size === roleFilterItems.length ? 0 : selectedRoleIds.size;
    const roleFilterActive = selectedRoleIds.size !== roleFilterItems.length;

    const tableData = useMemo(() => {
//<<<<<<< HEAD
        //// Like the ratio/max tabs, the person filter only hides rows: totals and averages stay global.
        //const keepSelectedUsers = (data: TableData): TableData => ({
            //...data,
            //rows: data.rows.filter((row) => !row.columns[0]?.userId || selectedUserIds.has(row.columns[0].userId!)),
        //});

        //if (displayMode === 'summary') {
            //return keepSelectedUsers(summaryToTableData(schedule, summaryColumns, generatedScheduleResultToEquity(schedule)));
//=======
        const keepSelected = (data: TableData, isColumnKept: (col: TableColumn) => boolean): TableData => {
            const keptColumnIndexes = data.columns
                .map((col, index) => ({ col, index }))
                .filter(({ col }) => isColumnKept(col))
                .map(({ index }) => index);

            return {
                columns: keptColumnIndexes.map((index) => data.columns[index]!),
                rows: data.rows
                    // The total row carries no userId and must stay visible.
                    .filter((row) => !row.columns[0]?.userId || selectedUserIds.has(row.columns[0].userId!))
                    .map((row) => ({ columns: keptColumnIndexes.map((index) => row.columns[index] ?? { value: "" }) })),
            };
        };
        const isRoleKept = (col: TableColumn) => col.type !== 'role' || selectedRoleIds.has(col.id!);
        const hasSelectedRole = (groupColumns: SummaryColumn[], type: string) => (col: TableColumn) => {
            if (!roleFilterActive || col.type !== type) {
                return true;
            }
            const roleIds = Object.keys(groupColumns.find((c) => c.id === col.id)?.mapOfNeedSettings ?? {});
            return roleIds.length === 0 || roleIds.some((roleId) => selectedRoleIds.has(roleId));
        };

        const visibleUserIds = selectedUserIds.size === userFilterItems.length ? undefined : selectedUserIds;

        if (displayMode === 'summary') {
            return keepSelected(
                summaryToTableData(schedule, summaryColumns, equityResult, visibleUserIds),
                hasSelectedRole(summaryColumns, 'summary')
            );
        }
        if (displayMode === 'roleGroup') {
            return keepSelected(
                roleGroupToTableData(schedule, roleGroupColumns, equityResult, roleGroupRefDays, roleGroupDesiredTargets, visibleUserIds),
                hasSelectedRole(roleGroupColumns, 'roleGroup')
            );
        }
        if (displayMode === 'annual') {
            const shownColumns = annualColumnsLoaded
                ? annualColumns
                : withDefaultAnnualColumns([], [], equityResult, roleOrders)?.columns ?? [];
            const hasAnnualSelectedRole = hasSelectedRole(shownColumns, 'annualCustom');
            return keepSelected(
                annualToTableData(schedule, equityResult, shownColumns, visibleUserIds),
                (col) => hasAnnualSelectedRole(col) && (col.type !== 'annualAdd' || annualColumnsLoaded)
            );
        }
        if (displayMode === 'weekly') {
            const visibleRoleIds = roleFilterActive ? selectedRoleIds : undefined;
            return keepSelected(weeklyToTableData(schedule, visibleUserIds, visibleRoleIds), () => true);
        }
        return keepSelected(
            visibleUserIds ? equityToTableData(equityResult, roleOrders, targets, visibleUserIds) : equityTableData,
            isRoleKept
        );
    }, [schedule, equityResult, roleOrders, equityTableData, targets, displayMode, summaryColumns, annualColumns, annualColumnsLoaded, roleGroupColumns, roleGroupRefDays, roleGroupDesiredTargets, userFilterItems, roleFilterActive, selectedUserIds, selectedRoleIds]);

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const topScrollRef = useRef<HTMLDivElement>(null);
    const [tableWidth, setTableWidth] = useState(0);
    const isSyncing = useRef(false);

    const handleTargetChange = (userId: string, columnId: string, value: string, mode: EquityDisplayMode) => {
        const numValue = Number.parseInt(value, 10);

        if (value === "" || (numValue >= 0 && numValue <= 999)) {
            const currentTarget = targets[userId]?.[columnId] || {};
            const newTarget = {
                ...currentTarget,
                [mode]: value === "" ? undefined : numValue
            };

            const shouldRemove = newTarget.ratio === undefined && newTarget.max === undefined;

            const newTargets = {
                ...targets,
                [userId]: {
                    ...targets[userId],
                    [columnId]: shouldRemove ? undefined : newTarget
                }
            };

            // Remove undefined values
            if (newTargets[userId]?.[columnId] === undefined) {
                delete newTargets[userId][columnId];
                if (Object.keys(newTargets[userId] || {}).length === 0) {
                    delete newTargets[userId];
                }
            }

            setTargets(newTargets);
            setHasUnsavedChanges(true);
            setSaveError(null);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setSaveError(null);
        try {
            const savedTargets = await updateEquityTargets(targets);
            setTargets(savedTargets);
            await toggleEquityActive('ratio', ratioActive);
            await toggleEquityActive('max', maxActive);
            await toggleEquityActive('summary', summaryActive);
            await toggleEquityActive('rolegroup', roleGroupActive);
            await updateSummaryColumns(summaryColumns);
            await updateRoleGroupConfig(roleGroupColumns, roleGroupRefDays, roleGroupDesiredTargets);
            if (annualColumnsLoaded && annualColumnsEdited) {
                await updateAnnualConfig(annualColumns, annualSeededRoleIds);
                setAnnualColumnsEdited(false);
            }
            setHasUnsavedChanges(false);
        } catch (err) {
            console.error("Failed to update equity targets:", err);
            setSaveError("Échec de la sauvegarde. Veuillez réessayer.");
        } finally {
            setIsSaving(false);
        }
    };

    // The annual tab has no activation switch, so it is never dimmed.
    const activeForMode = displayMode === 'ratio' ? ratioActive
        : displayMode === 'max' ? maxActive
            : displayMode === 'summary' ? summaryActive
                : displayMode === 'roleGroup' ? roleGroupActive
                    : true;

    const openNewSummaryColumn = () => {
        setEditingColumnId(null);
        setSummaryModalOpen(true);
    };

    const openEditSummaryColumn = (columnId: string) => {
        setEditingColumnId(columnId);
        setSummaryModalOpen(true);
    };

    const handleSummaryColumnConfirm = (column: SummaryColumn) => {
        setSummaryColumns((prev) => {
            const idx = prev.findIndex((c) => c.id === column.id);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = column;
                return next;
            }
            return [...prev, column];
        });
        setHasUnsavedChanges(true);
        setSaveError(null);
    };

    const handleSummaryColumnDelete = (columnId: string) => {
        setSummaryColumns((prev) => prev.filter((c) => c.id !== columnId));
        setEditingColumnId(null);
        setHasUnsavedChanges(true);
        setSaveError(null);
    };

    const moveColumn = <T extends SummaryColumn>(
        setColumns: React.Dispatch<React.SetStateAction<T[]>>,
        columnId: string,
        direction: -1 | 1
    ) => {
        setColumns((prev) => {
            const sorted = [...prev].sort((a, b) => a.order - b.order);
            const idx = sorted.findIndex((c) => c.id === columnId);
            const targetIdx = idx + direction;
            if (idx < 0 || targetIdx < 0 || targetIdx >= sorted.length) {
                return prev;
            }
            const [moved] = sorted.splice(idx, 1);
            sorted.splice(targetIdx, 0, moved!);
            return sorted.map((c, i) => ({ ...c, order: i }));
        });
        setHasUnsavedChanges(true);
    };

    const moveSummaryColumn = (columnId: string, direction: -1 | 1) => moveColumn(setSummaryColumns, columnId, direction);

    const openNewAnnualColumn = () => {
        setEditingAnnualColumnId(null);
        setAnnualModalOpen(true);
    };

    const openEditAnnualColumn = (columnId: string) => {
        setEditingAnnualColumnId(columnId);
        setAnnualModalOpen(true);
    };

    const handleAnnualColumnConfirm = (column: SummaryColumn) => {
        setAnnualColumns((prev) => {
            const idx = prev.findIndex((c) => c.id === column.id);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = column;
                return next;
            }
            return [...prev, column];
        });
        setAnnualColumnsEdited(true);
        setHasUnsavedChanges(true);
        setSaveError(null);
    };

    const handleAnnualColumnDelete = (columnId: string) => {
        setAnnualColumns((prev) => prev.filter((c) => c.id !== columnId));
        setEditingAnnualColumnId(null);
        setAnnualColumnsEdited(true);
        setHasUnsavedChanges(true);
        setSaveError(null);
    };

    const moveAnnualColumn = (columnId: string, direction: -1 | 1) => {
        moveColumn(setAnnualColumns, columnId, direction);
        setAnnualColumnsEdited(true);
    };

    const openNewRoleGroupColumn = () => {
        setEditingRoleGroupColumnId(null);
        setRoleGroupModalOpen(true);
    };

    const openEditRoleGroupColumn = (columnId: string) => {
        setEditingRoleGroupColumnId(columnId);
        setRoleGroupModalOpen(true);
    };

    const handleRoleGroupColumnConfirm = (column: SummaryColumn) => {
        const incoming = column as RoleGroupColumn;
        setRoleGroupColumns((prev) => {
            const existing = prev.find((c) => c.id === column.id);
            const baseTargets = incoming.targets ?? existing?.targets ?? {};
            let targets: Record<string, number> = baseTargets;
            if (schedule?.employees?.length) {
                const eligibleUserIds = new Set(
                    schedule.employees
                        .filter((emp) => emp.roles?.some((r) => column.mapOfNeedSettings?.[r.id]))
                        .map((emp) => emp.userId)
                );
                targets = {};
                Object.entries(baseTargets).forEach(([userId, val]) => {
                    if (eligibleUserIds.has(userId)) {
                        targets[userId] = val;
                    }
                });
            }
            const merged: RoleGroupColumn = { ...column, targets };
            const idx = prev.findIndex((c) => c.id === column.id);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = merged;
                return next;
            }
            return [...prev, merged];
        });
        setHasUnsavedChanges(true);
        setSaveError(null);
    };

    const handleRoleGroupPrefill = (column: SummaryColumn) => {
        const equityResult = generatedScheduleResultToEquity(schedule);
        const targets = computeRoleGroupPrefill(
            schedule,
            column,
            equityResult,
            roleGroupRefDays,
            roleGroupDesiredTargets,
            needsData
        );
        if (!targets) {
            handleRoleGroupColumnConfirm(column);
            setSaveError("Aucun besoin défini pour les rôles et jours sélectionnés.");
            return;
        }

        const merged: RoleGroupColumn = { ...column, targets };
        handleRoleGroupColumnConfirm(merged);
    };

    const handleRoleGroupColumnDelete = (columnId: string) => {
        setRoleGroupColumns((prev) => prev.filter((c) => c.id !== columnId));
        setEditingRoleGroupColumnId(null);
        setHasUnsavedChanges(true);
        setSaveError(null);
    };

    const moveRoleGroupColumn = (columnId: string, direction: -1 | 1) => moveColumn(setRoleGroupColumns, columnId, direction);

    const handleRoleGroupTargetChange = (columnId: string, userId: string, value: string) => {
        const numValue = Number.parseInt(value, 10);
        if (value !== "" && !(numValue >= 0 && numValue <= 999)) {
            return;
        }
        setRoleGroupColumns((prev) =>
            prev.map((col) => {
                if (col.id !== columnId) {
                    return col;
                }
                const nextTargets = { ...col.targets };
                if (value === "") {
                    delete nextTargets[userId];
                } else {
                    nextTargets[userId] = numValue;
                }
                return { ...col, targets: nextTargets };
            })
        );
        setHasUnsavedChanges(true);
        setSaveError(null);
    };

    const handleDesiredTargetChange = (userId: string, value: string) => {
        const numValue = Number.parseInt(value, 10);
        if (value !== "" && !(numValue >= 0 && numValue <= 999)) {
            return;
        }
        setRoleGroupDesiredTargets((prev) => {
            const next = { ...prev };
            if (value === "") {
                delete next[userId];
            } else {
                next[userId] = numValue;
            }
            return next;
        });
        setHasUnsavedChanges(true);
        setSaveError(null);
    };

    const handleRefDaysConfirm = (days: number) => {
        if (days !== roleGroupRefDays) {
            setRoleGroupRefDays(days);
            setHasUnsavedChanges(true);
            setSaveError(null);
        }
    };

    useEffect(() => {
        if (tableContainerRef.current) {
            setTableWidth(tableContainerRef.current.scrollWidth);
        }
    }, [tableData]);

    useEffect(() => {
        const handleResize = () => {
            if (tableContainerRef.current) {
                setTableWidth(tableContainerRef.current.scrollWidth);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleScroll = (source: 'top' | 'table') => {
        if (isSyncing.current) return;
        isSyncing.current = true;

        if (source === 'top' && topScrollRef.current && tableContainerRef.current) {
            tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        } else if (source === 'table' && tableContainerRef.current && topScrollRef.current) {
            topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
        }

        requestAnimationFrame(() => {
            isSyncing.current = false;
        });
    };

    return (
        <div className="w-full py-4">
            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 mb-4">
                <div className="flex flex-wrap items-center gap-3">
                    <SegmentedTabs
                        options={[
                            { value: 'ratio', label: 'Objectif de ratio en pourcent' },
                            { value: 'max', label: 'Quantitée maximale désirée' },
                            { value: 'roleGroup', label: 'Objectif groupe de rôles' },
                            { value: 'summary', label: 'Bilan' },
                            { value: 'annual', label: 'Annuelle' },
                            { value: 'weekly', label: 'Semaine' },
                        ]}
                        value={displayMode}
                        onChange={setDisplayMode}
                    />

                    {displayMode !== 'annual' && displayMode !== 'weekly' && <Switch
                        checked={activeForMode}
                        onChange={(val) => {
                            if (displayMode === 'ratio') {
                                setRatioActive(val);
                            } else if (displayMode === 'max') {
                                setMaxActive(val);
                            } else if (displayMode === 'summary') {
                                setSummaryActive(val);
                            } else {
                                setRoleGroupActive(val);
                            }
                            setHasUnsavedChanges(true);
                            setSaveError(null);
                        }}
                    />}

                    <div className="flex items-center gap-1">
                        <TimelineFilterOverlay
                            isOpen={userFilterOpen}
                            onOpenChange={setUserFilterOpen}
                            items={userFilterItems}
                            selectedItems={selectedUserIds}
                            onSelectionChange={setUserSelection}
                            title="Sélection des personnes"
                        >
                            <FilterButton label="Personne" active={userFilterOpen} count={userFilterCount}/>
                        </TimelineFilterOverlay>

                        <TimelineFilterOverlay
                            isOpen={roleFilterOpen}
                            onOpenChange={setRoleFilterOpen}
                            items={roleFilterItems}
                            selectedItems={selectedRoleIds}
                            onSelectionChange={setRoleSelection}
                            title="Sélection des rôles"
                        >
                            <FilterButton label="Rôle" active={roleFilterOpen} count={roleFilterCount}/>
                        </TimelineFilterOverlay>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    {displayMode === 'roleGroup' && (
                        <div className="flex items-center gap-2 text-sm whitespace-nowrap">
                            <span className="font-medium text-gray-700">Quantité jour référence :</span>
                            <span className="font-semibold text-gray-900">{roleGroupRefDays}</span>
                            <IconButton
                                size="sm"
                                className="p-1.5"
                                icon={<Pencil size={16}/>}
                                ariaLabel="Modifier la quantité jour référence"
                                title="Modifier la quantité jour référence"
                                onClick={() => setRefDaysModalOpen(true)}
                            />
                        </div>
                    )}
                    <Button
                        type="primary"
                        icon={<Settings size={20}/>}
                        onClick={() => setEquityConfigOpen(true)}
                    />
                    <div className="flex flex-col items-end gap-2">
                        <button
                            onClick={handleSave}
                            disabled={!hasUnsavedChanges || isSaving}
                            className={`px-4 py-2 rounded-full text-base whitespace-nowrap transition-colors ${
                                hasUnsavedChanges && !isSaving
                                    ? "text-white cursor-pointer"
                                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                            }`}
                            style={hasUnsavedChanges && !isSaving ? { backgroundColor: 'dodgerblue' } : {}}
                        >
                            {isSaving ? "Sauvegarde..." : "Sauvegarder les modifications"}
                        </button>
                        {saveError && (
                            <span className="text-red-600 text-sm">{saveError}</span>
                        )}
                    </div>
                </div>
            </div>

            <div className="relative">
                {!activeForMode && (
                    <div className="absolute inset-0 bg-gray-200/60 pointer-events-none z-30 rounded-lg"/>
                )}
                <div
                    ref={topScrollRef}
                    className="overflow-x-auto overflow-y-hidden mb-1"
                    onScroll={() => handleScroll('top')}
                >
                    <div style={{ width: tableWidth, height: '1px' }}></div>
                </div>

                <div
                    ref={tableContainerRef}
                    className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-300px)] pb-2"
                    onScroll={() => handleScroll('table')}
                >
                    <table className="w-full border-collapse border border-gray-300 text-center text-sm relative">
                        <thead>
                        <tr>
                            {tableData.columns.map((col, index) => {
                                const isSummaryCol = displayMode === 'summary' && col.type === 'summary';
                                const isSummaryAdd = displayMode === 'summary' && col.type === 'summaryAdd';
                                const isRoleGroupCol = displayMode === 'roleGroup' && col.type === 'roleGroup';
                                const isRoleGroupAdd = displayMode === 'roleGroup' && col.type === 'roleGroupAdd';
                                const isAnnualCol = displayMode === 'annual' && col.type === 'annualCustom' && annualColumnsLoaded;
                                const isAnnualAdd = displayMode === 'annual' && col.type === 'annualAdd';
                                const isEditableCol = isSummaryCol || isRoleGroupCol || isAnnualCol;
                                const isAddCol = isSummaryAdd || isRoleGroupAdd || isAnnualAdd;
                                const clickable = (isEditableCol || isAddCol) && activeForMode && !isSaving;
                                const onAdd = isRoleGroupAdd ? openNewRoleGroupColumn : isAnnualAdd ? openNewAnnualColumn : openNewSummaryColumn;
                                const openEditEditableColumn = isRoleGroupCol ? openEditRoleGroupColumn : isAnnualCol ? openEditAnnualColumn : openEditSummaryColumn;
                                const onHeaderClick = !clickable
                                    ? undefined
                                    : isAddCol
                                        ? onAdd
                                        : () => openEditEditableColumn(col.id!);
                                const colGroupType = isRoleGroupCol ? 'roleGroup' : isSummaryCol ? 'summary' : isAnnualCol ? 'annualCustom' : undefined;
                                const canMoveLeft = !!colGroupType && !roleFilterActive && tableData.columns[index - 1]?.type === colGroupType;
                                const canMoveRight = !!colGroupType && !roleFilterActive && tableData.columns[index + 1]?.type === colGroupType;
                                const moveEditableColumn = isRoleGroupCol ? moveRoleGroupColumn : isAnnualCol ? moveAnnualColumn : moveSummaryColumn;
                                return (
                                    <th
                                        key={index}
                                        onClick={onHeaderClick}
                                        onKeyDown={clickable ? (e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onHeaderClick!();
                                            }
                                        } : undefined}
                                        role={clickable ? 'button' : undefined}
                                        tabIndex={clickable ? 0 : undefined}
                                        title={isEditableCol ? 'Modifier la colonne' : isAddCol ? 'Ajouter une colonne' : undefined}
                                        className={`border border-gray-300 bg-gray-100 font-semibold sticky top-0 z-10 ${isEditableCol ? "p-0" : "px-2 py-1"} ${index === 0 ? "min-w-[150px] left-0 z-20" : "min-w-[80px]"} ${clickable ? "cursor-pointer hover:bg-gray-200" : ""} ${(isEditableCol || isAddCol) && !activeForMode ? "opacity-50" : ""}`}
                                    >
                                        {isAddCol ? (
                                            <IconButton
                                                size="sm"
                                                icon={<Plus size={18}/>}
                                                ariaLabel="Ajouter une colonne"
                                                title="Ajouter une colonne"
                                                disabled={!activeForMode || isSaving}
                                                onClick={(e) => { e.stopPropagation(); onAdd(); }}
                                                tabIndex={-1}
                                            />
                                        ) : isEditableCol ? (
                                            <EditableColumnHeader
                                                label={col.label}
                                                disabled={!activeForMode || isSaving}
                                                canMoveLeft={canMoveLeft}
                                                canMoveRight={canMoveRight}
                                                onMoveLeft={() => moveEditableColumn(col.id!, -1)}
                                                onMoveRight={() => moveEditableColumn(col.id!, 1)}
                                                onEdit={() => openEditEditableColumn(col.id!)}
                                            />
                                        ) : (
                                            col.label
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                        </thead>
                        <tbody>
                        {tableData.rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className="row-hover-frame">
                                {row.columns.map((cell, cellIndex) => {
                                    if (displayMode === 'summary' || displayMode === 'annual' || displayMode === 'weekly') {
                                        const isSummaryNameColumn = cellIndex === 0;
                                        const summaryTotalClasses = cell.isTotal ? "bg-gray-100 font-semibold" : "";
                                        return (
                                            <td
                                                key={cellIndex}
                                                className={`border border-gray-300 px-2 py-1 ${isSummaryNameColumn ? `sticky left-0 z-10 font-bold ${cell.isTotal ? "" : "bg-white"}` : ""} ${summaryTotalClasses}`}
                                                style={cell.bgColor ? { backgroundColor: cell.bgColor } : {}}
                                            >
                                                {cell.value}
                                            </td>
                                        );
                                    }
                                    if (displayMode === 'roleGroup') {
                                        const isRoleGroupNameColumn = cellIndex === 0;
                                        const isDesiredColumn = tableData.columns[cellIndex]?.type === 'roleGroupDesired';
                                        const totalClasses = cell.isTotal ? "bg-gray-100 font-semibold" : "";
                                        return (
                                            <td
                                                key={cellIndex}
                                                className={`border border-gray-300 px-2 py-1 ${isRoleGroupNameColumn ? `sticky left-0 z-10 font-bold ${cell.isTotal ? "" : "bg-white"}` : ""} ${totalClasses}`}
                                                style={cell.bgColor ? { backgroundColor: cell.bgColor } : {}}
                                            >
                                                {cell.isTotal ? (
                                                    cell.value
                                                ) : isDesiredColumn ? (
                                                    <EquityTargetCell
                                                        displayValue={cell.value}
                                                        inputValue={cell.desiredTarget ?? ""}
                                                        disabled={!activeForMode}
                                                        onChange={(value) => handleDesiredTargetChange(cell.userId!, value)}
                                                    />
                                                ) : cell.eligible ? (
                                                    <EquityTargetCell
                                                        displayValue={cell.value}
                                                        inputValue={cell.roleGroupTarget ?? ""}
                                                        disabled={!activeForMode}
                                                        onChange={(value) => handleRoleGroupTargetChange(cell.columnId!, cell.userId!, value)}
                                                    />
                                                ) : (
                                                    cell.value
                                                )}
                                            </td>
                                        );
                                    }
                                    const isNameColumn = cellIndex === 0;
                                    const isTotalRow = cell.value === "Total/Moyenne";
                                    var hasTarget = cell.userId && cell.columnId && !isTotalRow;
                                    const column = tableData.columns[cellIndex];
                                    const isTotalShiftsColumn = column?.type === "totalShifts";
                                    const isHolidayShiftsColumn = column?.type === "holidayShifts";
                                    const isTotalWeekendColumn = column?.type === "weekenedShifts";
                                    const defaultTargetValue = isTotalShiftsColumn ? "" : (displayMode === 'ratio' ? 100 : "");
                                    const storedTargetValue = displayMode === 'ratio' ? cell.target?.ratio : cell.target?.max;
                                    // The backend encodes "no target" as a negative value; show an empty field for it.
                                    const currentTargetValue = typeof storedTargetValue === "number" && storedTargetValue >= 0
                                        ? storedTargetValue
                                        : undefined;
                                    if (isTotalShiftsColumn) hasTarget = false;
                                    if (isTotalWeekendColumn) hasTarget = false;
                                    if (isHolidayShiftsColumn) hasTarget = false;
                                    const isIncompetent = cell.eligible === false;
                                    if (isIncompetent) hasTarget = false;
                                    const exceedsMax = displayMode === 'max' && hasTarget
                                        && currentTargetValue !== undefined && Number(cell.value) > currentTargetValue;

                                    return (
                                        <td
                                            key={cellIndex}
                                            className={`border border-gray-300 px-2 py-1 ${isNameColumn ? "sticky left-0 bg-white z-10 font-bold" : ""}`}
                                            style={isIncompetent ? { backgroundColor: "#fee2e2" } : exceedsMax ? { backgroundColor: "#f88f96" } : (cell.bgColor ? { backgroundColor: cell.bgColor } : {})}
                                        >
                                            {hasTarget ? (
                                                <EquityTargetCell
                                                    displayValue={cell.value}
                                                    inputValue={currentTargetValue ?? defaultTargetValue}
                                                    disabled={!activeForMode}
                                                    onChange={(value) => handleTargetChange(cell.userId!, cell.columnId!, value, displayMode)}
                                                />
                                            ) : isIncompetent ? (
                                                <span className="text-red-700 font-semibold">{cell.value}</span>
                                            ) : (
                                                cell.value
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {displayMode === 'summary' && (
                <SummaryColumnModal
                    open={summaryModalOpen}
                    onClose={() => setSummaryModalOpen(false)}
                    column={editingColumnId ? summaryColumns.find((c) => c.id === editingColumnId) : undefined}
                    defaultOrder={summaryColumns.length ? Math.max(...summaryColumns.map((c) => c.order)) + 1 : 0}
                    schedule={schedule}
                    onConfirm={handleSummaryColumnConfirm}
                    onDelete={handleSummaryColumnDelete}
                />
            )}

            {displayMode === 'annual' && (
                <SummaryColumnModal
                    open={annualModalOpen}
                    onClose={() => setAnnualModalOpen(false)}
                    column={editingAnnualColumnId ? annualColumns.find((c) => c.id === editingAnnualColumnId) : undefined}
                    defaultOrder={annualColumns.length ? Math.max(...annualColumns.map((c) => c.order)) + 1 : 0}
                    schedule={schedule}
                    onConfirm={handleAnnualColumnConfirm}
                    onDelete={handleAnnualColumnDelete}
                />
            )}

            {displayMode === 'roleGroup' && (
                <SummaryColumnModal
                    open={roleGroupModalOpen}
                    onClose={() => setRoleGroupModalOpen(false)}
                    column={editingRoleGroupColumnId ? roleGroupColumns.find((c) => c.id === editingRoleGroupColumnId) : undefined}
                    defaultOrder={roleGroupColumns.length ? Math.max(...roleGroupColumns.map((c) => c.order)) + 1 : 0}
                    schedule={schedule}
                    onConfirm={handleRoleGroupColumnConfirm}
                    onDelete={handleRoleGroupColumnDelete}
                    onPrefill={handleRoleGroupPrefill}
                    prefillReady={!!needsData}
                />
            )}

            {displayMode === 'roleGroup' && (
                <RefDaysModal
                    open={refDaysModalOpen}
                    value={roleGroupRefDays}
                    onClose={() => setRefDaysModalOpen(false)}
                    onConfirm={handleRefDaysConfirm}
                />
            )}

            <EquityConfigModal
                open={equityConfigOpen}
                onClose={() => setEquityConfigOpen(false)}
                roles={schedule?.roles ?? []}
            />
        </div>
    );
};

export default SchedulerEquity;
